import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
const Skills: React.FC = () => {
  const [openAccordion, setOpenAccordion] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const skills = [{
    id: 1,
    title: 'Strategic Blueprint Design',
    description: 'Every successful project starts with a data-driven plan. I conduct forensic research into your market, your ideal customers, and your competitors to build a strategic foundation that ensures we are always moving in the right direction. This is the blueprint that guarantees your growth is intentional, not accidental.'
  }, {
    id: 2,
    title: 'Full-Funnel Ecosystem Architecture',
    description: "I don't just build websites or run ads—I build the complete, interconnected system that turns strangers into clients. From initial awareness to final conversion, I architect every touchpoint to work in perfect harmony, creating a seamless customer journey that maximizes your revenue at every stage."
  }, {
    id: 3,
    title: 'Systemization & Process Building',
    description: 'Growth without systems is chaos. I bring order to your growth, creating clear processes, automations, and workflows that ensure your success is scalable and sustainable. My systems allow you to manage increased demand without sacrificing quality or burning out your team.'
  }, {
    id: 4,
    title: 'Brand & Identity Development',
    description: "Your brand is your business's reputation, and I build a professional, trustworthy brand that connects with your ideal customers on an emotional level. From logos and color palettes to messaging and tone, I create a cohesive identity that positions you as the obvious choice in your market."
  }, {
    id: 5,
    title: 'WordPress & Web Development Foundation',
    description: 'I build your 24/7 digital salesperson—a professional, modern website that is not only visually stunning but also strategically designed to convert visitors into paying clients. Every page, every element, and every word is crafted with conversion in mind.'
  }, {
    id: 6,
    title: 'LinkedIn Authority Building',
    description: "LinkedIn is the most powerful B2B platform, and it's my specialty for establishing you as a leader in your industry. I optimize your profile, craft compelling content, and deploy strategic networking tactics to attract high-value clients and position you as the go-to expert."
  }, {
    id: 7,
    title: 'Social Media Advertising & Organic Marketing',
    description: "I execute targeted campaigns that build your audience and drive traffic across all major platforms. Whether it's Meta Ads, organic content strategies, or influencer partnerships, I deploy the right tactics to reach your ideal customers where they spend their time online."
  }, {
    id: 8,
    title: 'Data-Driven Performance Marketing',
    description: "Every dollar you invest must deliver a measurable return. I use data to track performance, identify what's working, eliminate what's not, and continuously optimize your campaigns to ensure your marketing budget delivers the highest possible ROI."
  }, {
    id: 9,
    title: 'High-Conversion Copywriting',
    description: 'Words sell. I write the words that connect with your audience on an emotional level and persuade them to act. From website copy and ad scripts to email sequences and landing pages, I craft messaging that drives conversions and revenue.'
  }, {
    id: 10,
    title: 'Transparent Project Management',
    description: "You will never be left in the dark. I ensure a calm, organized, and professional experience from start to finish with clear communication, regular updates, and transparent reporting. You will always know exactly what is happening, why it's happening, and what results we are achieving."
  }];
  const toolkitCategories = [{
    id: 1,
    title: '01. Strategy & Research',
    items: ['Service/Product Market Analysis', 'Targeted Audience Analysis & Reporting', 'Forensic Competitor Analysis', 'Digital Presence & Brand Audits', 'Sales & Marketing Strategy Development'],
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80'
  }, {
    id: 2,
    title: '02. Branding & Content',
    items: ['Professional Logo Design', 'Color Palette & Font Systemization', 'High-Quality Brand Mockups', 'SEO-Optimized Content Writing'],
    image: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1171&q=80'
  }, {
    id: 3,
    title: '03. Web & Systems Development',
    items: ['WordPress Website Development (6-7 Pages)', 'WhatsApp Business Integration', 'Custom CRM Creation & Systemization'],
    image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80'
  }, {
    id: 4,
    title: '04. Social Media & Advertising',
    items: ['LinkedIn Profile & Company Page Optimization', 'Organic Social Media Marketing & Management', 'Meta Ads (Facebook & Instagram) Campaign Management', 'Ad Creative & Ad Copy Development', 'Performance Testing & Optimization'],
    image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80'
  }];
  const workAttributes = [{
    id: 1,
    title: '01. A STRATEGIC PARTNER',
    description: "I don't just execute tasks; I analyze your business goals to create a comprehensive plan that ensures we are always moving in the right direction."
  }, {
    id: 2,
    title: '02. METICULOUS ATTENTION TO DETAIL',
    description: 'I treat your business as if it were my own, ensuring every element is perfect and aligned with our strategy.'
  }, {
    id: 3,
    title: '03. TRANSPARENT & CLEAR COMMUNICATION',
    description: "You will never be left in the dark. I believe in maintaining open, honest, and regular communication so you are always confident in the project's progress."
  }, {
    id: 4,
    title: '04. A RELENTLESS FOCUS ON ROI',
    description: 'My ultimate goal is to grow your business. I am obsessed with tracking performance and ensuring that your investment yields a real, measurable financial return.'
  }];
  return <div className="min-h-screen pt-32 pb-20" data-id="element-413">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center" data-id="element-414">
        <motion.h1 className="text-4xl md:text-6xl font-bold mb-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} data-id="element-415">
          THE ARCHITECT'S TOOLKIT
        </motion.h1>
        <motion.p className="font-thin text-lg max-w-3xl mx-auto" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} data-id="element-416">
          This is the complete overview of my capabilities. Below, you will find
          the strategic expertise, the technical skills, and the professional
          attributes I bring to every partnership to build your complete,
          revenue-focused digital ecosystem.
        </motion.p>
      </section>
      {/* Core Expertise */}
      <section className="py-20" ref={containerRef} data-id="element-417">
        <div className="container mx-auto px-4" data-id="element-418">
          <h2 className="text-3xl md:text-5xl font-bold mb-12 text-center" data-id="element-419">
            MY CORE EXPERTISE
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-420">
            {skills.map((skill, index) => <SkillItem key={skill.id} title={skill.title} description={skill.description} index={index} data-id="element-421" />)}
          </div>
        </div>
      </section>
      {/* Technical Toolkit */}
      <section className="container mx-auto px-4 py-20" data-id="element-422">
        <h2 className="text-3xl md:text-5xl font-bold mb-12 text-center" data-id="element-423">
          MY TECHNICAL TOOLKIT
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-424">
          <div className="space-y-6" data-id="element-425">
            {toolkitCategories.map((category, index) => <ToolkitAccordion key={category.id} title={category.title} items={category.items} isOpen={openAccordion === index} onClick={() => setOpenAccordion(index)} data-id="element-426" />)}
          </div>
          <div className="sticky top-32 h-[400px] rounded-2xl overflow-hidden" data-id="element-427">
            <img src={toolkitCategories[openAccordion].image} alt={toolkitCategories[openAccordion].title} className="w-full h-full object-cover transition-opacity duration-500" data-id="element-428" />
          </div>
        </div>
      </section>
      {/* How I Work */}
      <section className="container mx-auto px-4 py-20" data-id="element-429">
        <h2 className="text-3xl md:text-5xl font-bold mb-12 text-center" data-id="element-430">
          HOW I WORK
        </h2>
        <div className="space-y-8" data-id="element-431">
          {workAttributes.map(attribute => <WorkAttributeAccordion key={attribute.id} title={attribute.title} description={attribute.description} data-id="element-432" />)}
        </div>
      </section>
    </div>;
};
interface SkillItemProps {
  title: string;
  description: string;
  index: number;
}
const SkillItem: React.FC<SkillItemProps> = ({
  title,
  description,
  index
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [50, 0, 0, -50]);
  return <motion.div ref={ref} style={{
    opacity,
    y
  }} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="bg-white dark:bg-[#121212] p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-transparent hover:border-[#39FF14]" data-id="element-433">
      <div className="flex items-start space-x-4" data-id="element-434">
        <div className="flex-shrink-0" data-id="element-435">
          <div className="w-3 h-3 bg-[#39FF14] rounded-full mt-2" data-id="element-436"></div>
        </div>
        <div className="flex-1" data-id="element-437">
          <h3 className="text-xl font-bold mb-3 text-[#121212] dark:text-white" data-id="element-438">
            {title}
          </h3>
          <p className="font-normal text-base leading-relaxed text-[#121212] dark:text-gray-300" data-id="element-439">
            {description}
          </p>
        </div>
      </div>
    </motion.div>;
};
interface ToolkitAccordionProps {
  title: string;
  items: string[];
  isOpen: boolean;
  onClick: () => void;
}
const ToolkitAccordion: React.FC<ToolkitAccordionProps> = ({
  title,
  items,
  isOpen,
  onClick
}) => {
  return <div className="border-b border-gray-200 dark:border-gray-700" data-id="element-440">
      <button className="flex justify-between items-center w-full py-4 text-left" onClick={onClick} data-id="element-441">
        <h3 className="text-lg font-bold" data-id="element-442">{title}</h3>
        <ChevronDownIcon className={`w-5 h-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} data-id="element-443" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} className="overflow-hidden" data-id="element-444">
        <div className="pb-4" data-id="element-445">
          <ul className="space-y-2" data-id="element-446">
            {items.map((item, index) => <li key={index} className="flex items-start" data-id="element-447">
                <span className="text-[#39FF14] mr-2" data-id="element-448">✓</span>
                <p className="font-thin" data-id="element-449">{item}</p>
              </li>)}
          </ul>
        </div>
      </motion.div>
    </div>;
};
interface WorkAttributeAccordionProps {
  title: string;
  description: string;
}
const WorkAttributeAccordion: React.FC<WorkAttributeAccordionProps> = ({
  title,
  description
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 dark:border-gray-700" data-id="element-450">
      <button className="flex justify-between items-center w-full py-6 text-left" onClick={() => setIsOpen(!isOpen)} data-id="element-451">
        <h3 className="text-xl font-bold" data-id="element-452">{title}</h3>
        <ChevronDownIcon className={`w-6 h-6 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} data-id="element-453" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} className="overflow-hidden" data-id="element-454">
        <div className="pb-6 font-thin text-lg" data-id="element-455">{description}</div>
      </motion.div>
    </div>;
};
export default Skills;